import React from "react";
import { Link } from "react-router-dom";
import { formatCOP } from "../../lib/apiClient";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { FormField } from "../../components/form/FormField";

const MODALIDAD_LABELS = {
  licitacion_publica: "Licitación pública",
  seleccion_abreviada: "Selección abreviada",
  concurso_meritos: "Concurso de méritos",
  contratacion_directa: "Contratación directa",
  minima_cuantia: "Mínima cuantía",
  app: "Asociación público-privada (APP)",
  desconocida: "No estoy seguro",
};

const FORMA_PAGO_LABELS = {
  pago_unico_final: "Pago único final",
  anticipos: "Con anticipos",
  contraentrega_hitos: "Contra entrega por hitos",
  mixto: "Mixto",
  por_definir: "Por definir",
};

const GARANTIAS_OPTIONS = [
  { key: "cumplimiento", label: "Cumplimiento" },
  { key: "calidad_correcto_funcionamiento", label: "Calidad y correcto funcionamiento" },
  { key: "pago_salarios_prestaciones", label: "Pago de salarios y prestaciones" },
  { key: "responsabilidad_civil_extracontractual", label: "Responsabilidad civil extracontractual" },
  { key: "estabilidad_obra", label: "Estabilidad de obra" },
  { key: "ninguna", label: "Ninguna" },
];

/**
 * Formulario de intake N2 con todos los campos requeridos y opcionales.
 */
export function IntakeN2Form({
  form,
  setField,
  toggleGarantia,
  confirmFinal,
  setConfirmFinal,
  errorMsg,
  submitting,
  onSubmit,
  caseId,
  scopePrice,
  existingIntake,
}) {
  return (
    <form onSubmit={onSubmit} className="space-y-6">
      <section className="border border-gray-200 bg-white p-6">
        <h2 className="font-serif text-xl text-gray-900 mb-1">Datos adicionales</h2>
        <p className="text-xs text-gray-500 mb-5">
          Necesitamos información más detallada para preparar el Kit Documental. Algunos campos se
          completan automáticamente con base en tu intake inicial; otros requieren confirmación o
          detalle adicional.
        </p>

        {/* Required group */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          <FormField label="Presupuesto definitivo (COP) *" testid="f-presupuesto">
            <Input
              type="number"
              value={form.presupuesto_definitivo_cop}
              onChange={(e) => setField("presupuesto_definitivo_cop", e.target.value)}
              min="0"
              step="1000"
              data-testid="n2-presupuesto"
            />
          </FormField>
          <FormField label="Plazo de ejecución (meses) *" testid="f-plazo">
            <Input
              type="number"
              value={form.plazo_ejecucion_meses}
              onChange={(e) => setField("plazo_ejecucion_meses", e.target.value)}
              min="0.25"
              step="0.25"
              data-testid="n2-plazo"
            />
          </FormField>
          <FormField label="Departamento *" testid="f-departamento">
            <Input
              value={form.lugar_ejecucion_departamento}
              onChange={(e) => setField("lugar_ejecucion_departamento", e.target.value)}
              data-testid="n2-departamento"
            />
          </FormField>
          <FormField label="Municipio *" testid="f-municipio">
            <Input
              value={form.lugar_ejecucion_municipio}
              onChange={(e) => setField("lugar_ejecucion_municipio", e.target.value)}
              data-testid="n2-municipio"
            />
          </FormField>
        </div>

        <FormField
          label="Productos / servicios a contratar *"
          testid="f-productos"
          help={`${(form.productos_servicios_detalle || "").length} / 6000 caracteres (mín. 50)`}
        >
          <Textarea
            value={form.productos_servicios_detalle}
            onChange={(e) => setField("productos_servicios_detalle", e.target.value)}
            rows={4}
            maxLength={6000}
            placeholder="Describe en detalle qué se va a contratar, especificaciones técnicas mínimas, cantidades y unidades de medida."
            data-testid="n2-productos"
          />
        </FormField>

        <FormField
          label="Cantidades y unidades de medida *"
          testid="f-cantidades"
          help={`${(form.cantidades_unidades || "").length} / 4000 caracteres (mín. 20)`}
        >
          <Textarea
            value={form.cantidades_unidades}
            onChange={(e) => setField("cantidades_unidades", e.target.value)}
            rows={3}
            maxLength={4000}
            placeholder="Ej: 10 computadores portátiles, 5 impresoras láser, 20 escritorios."
            data-testid="n2-cantidades"
          />
        </FormField>

        <FormField label="Modalidad de contratación *" testid="f-modalidad">
          <select
            value={form.modalidad_contratacion}
            onChange={(e) => setField("modalidad_contratacion", e.target.value)}
            className="w-full border border-gray-300 px-3 py-2 rounded-sm bg-white"
            data-testid="n2-modalidad"
          >
            {Object.entries(MODALIDAD_LABELS).map(([k, v]) => (
              <option key={k} value={k}>
                {v}
              </option>
            ))}
          </select>
        </FormField>
      </section>

      {/* Opcionales */}
      <section className="border border-gray-200 bg-white p-6">
        <h2 className="font-serif text-xl text-gray-900 mb-3">
          Información complementaria (opcional)
        </h2>

        <FormField label="Requisitos técnicos mínimos" testid="f-tecnicos">
          <Textarea
            value={form.requisitos_tecnicos_minimos || ""}
            onChange={(e) => setField("requisitos_tecnicos_minimos", e.target.value)}
            rows={2}
            maxLength={4000}
            data-testid="n2-tecnicos"
          />
        </FormField>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
          <FormField label="Forma de pago al contratista" testid="f-formapago">
            <select
              value={form.forma_pago_contratista}
              onChange={(e) => setField("forma_pago_contratista", e.target.value)}
              className="w-full border border-gray-300 px-3 py-2 rounded-sm bg-white"
              data-testid="n2-formapago"
            >
              {Object.entries(FORMA_PAGO_LABELS).map(([k, v]) => (
                <option key={k} value={k}>
                  {v}
                </option>
              ))}
            </select>
          </FormField>
          <FormField label="Detalle forma de pago" testid="f-formapago-detalle">
            <Input
              value={form.forma_pago_detalle || ""}
              onChange={(e) => setField("forma_pago_detalle", e.target.value)}
              placeholder="Ej: 30% anticipo, 70% contraentrega"
              data-testid="n2-formapago-detalle"
            />
          </FormField>
        </div>

        <FormField label="Garantías exigidas" testid="f-garantias" help="Selecciona las que apliquen">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mt-1">
            {GARANTIAS_OPTIONS.map((g) => (
              <label key={g.key} className="flex items-center gap-2 text-sm text-gray-700">
                <input
                  type="checkbox"
                  checked={form.garantias_exigidas.includes(g.key)}
                  onChange={() => toggleGarantia(g.key)}
                  data-testid={`n2-garantia-${g.key}`}
                />
                {g.label}
              </label>
            ))}
          </div>
        </FormField>

        <FormField label="Detalle garantías" testid="f-garantias-detalle">
          <Input
            value={form.garantias_detalle || ""}
            onChange={(e) => setField("garantias_detalle", e.target.value)}
            placeholder="Ej: cumplimiento 20% por vigencia + 6 meses"
            data-testid="n2-garantias-detalle"
          />
        </FormField>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
          <label className="flex items-center gap-2 text-sm text-gray-700">
            <input
              type="checkbox"
              checked={form.requiere_supervision}
              onChange={(e) => setField("requiere_supervision", e.target.checked)}
              data-testid="n2-supervision"
            />
            ¿Requiere supervisión técnica?
          </label>
          <label className="flex items-center gap-2 text-sm text-gray-700">
            <input
              type="checkbox"
              checked={form.requiere_interventoria}
              onChange={(e) => setField("requiere_interventoria", e.target.checked)}
              data-testid="n2-interventoria"
            />
            ¿Requiere interventoría?
          </label>
        </div>

        <FormField label="Detalle supervisión / interventoría" testid="f-supervision-detalle">
          <Textarea
            value={form.supervision_interventoria_detalle || ""}
            onChange={(e) => setField("supervision_interventoria_detalle", e.target.value)}
            rows={2}
            maxLength={2000}
            data-testid="n2-supervision-detalle"
          />
        </FormField>

        <FormField label="Comentarios adicionales" testid="f-comentarios">
          <Textarea
            value={form.comentarios_adicionales || ""}
            onChange={(e) => setField("comentarios_adicionales", e.target.value)}
            rows={3}
            maxLength={4000}
            placeholder="Cualquier otra información relevante para el caso."
            data-testid="n2-comentarios"
          />
        </FormField>
      </section>

      {/* Confirmación final */}
      <section className="border border-amber-200 bg-amber-50/40 p-4 rounded-sm">
        <label className="flex items-start gap-3 text-sm text-gray-800">
          <input
            type="checkbox"
            checked={confirmFinal}
            onChange={(e) => setConfirmFinal(e.target.checked)}
            className="mt-1"
            data-testid="n2-confirm-final"
          />
          <span>
            <strong>Confirmo</strong> que la información proporcionada es correcta. Entiendo que
            una vez iniciado el pago, no podré editar estos datos. Reconozco que el Kit Documental
            requiere revisión humana experta antes de la descarga.
          </span>
        </label>
      </section>

      {errorMsg && (
        <div
          className="text-sm text-rose-800 bg-rose-50 border border-rose-200 p-3 rounded-sm"
          data-testid="n2-error"
        >
          {errorMsg}
        </div>
      )}

      <div className="flex flex-wrap items-center gap-3 justify-between">
        <Link to={`/cases/${caseId}`} className="text-sm text-gray-500 hover:text-primary">
          Cancelar
        </Link>
        <Button
          type="submit"
          disabled={submitting || !confirmFinal}
          className="rounded-sm h-11"
          data-testid="n2-submit"
        >
          {submitting
            ? "Procesando…"
            : `Continuar al pago — ${formatCOP(scopePrice || 1200000)}`}
        </Button>
      </div>

      {existingIntake && (
        <p className="text-xs text-gray-500 text-right">
          Editando intake versión {existingIntake.version || 1}
        </p>
      )}
    </form>
  );
}
